// ReservationsReport — every reservation in the window, with breakdowns
// by status / billing-mode / source and a full Excel-able table.

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReportShell from "./ReportShell";
import { Panel, StatusBadge, Loading, fmtMoney, fmtDate } from "./OverviewReport";
import { StatTile, BarChart, PieChart } from "../../../components/charts";
import { reservationsUrl } from "../../../BaseURL";

const STATUS_COLORS = {
  pending: "#3a7bd5", confirmed: "#c8a05a", checked_in: "#4a7c59",
  checked_out: "#8a7060", cancelled: "#a93226", no_show: "#7a4a4a",
};

export default function ReservationsReport({ range, onRangeChange }) {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(reservationsUrl, {
          params: { from: range.from || undefined, to: range.to || undefined },
        });
        if (!cancelled) setReservations(r.data?.results || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range.from, range.to]);

  const filtered = useMemo(
    () => statusFilter === "all" ? reservations : reservations.filter(r => r.status === statusFilter),
    [reservations, statusFilter]
  );

  const stats = useMemo(() => {
    const STATUSES = ["pending","confirmed","checked_in","checked_out","cancelled","no_show"];
    const byStatus = STATUSES.map(s => ({
      label: s.replace("_", " "),
      value: reservations.filter(r => r.status === s).length,
      color: STATUS_COLORS[s],
    })).filter(s => s.value > 0);

    const bySource = aggregate(reservations, r => r.source || "walk_in");
    const byMode   = aggregate(reservations, r => r.billing_mode || "nightly");
    const total    = reservations.reduce((s, r) => s + (Number(r.total_amount) || 0), 0);
    const paid     = reservations.reduce((s, r) => s + (Number(r.paid_amount)  || 0), 0);

    return { byStatus, bySource, byMode, total, paid, balance: total - paid };
  }, [reservations]);

  const csv = useMemo(() => ({
    headers: ["Code","Group","Guest","Phone","Room","Check-in","Check-out","Nights/Hours","Status","Mode","Source","Total","Paid","Balance"],
    rows: filtered.map(r => [
      r.code, r.booking_group_code || "",
      r.guest_name, r.guest_phone || "",
      r.room_number || "",
      r.check_in_at || r.check_in_date,
      r.check_out_at || r.check_out_date,
      r.billing_mode === "hourly" ? `${r.hours}h` : r.nights,
      r.status, r.billing_mode || "nightly", r.source || "walk_in",
      Number(r.total_amount).toFixed(2),
      Number(r.paid_amount).toFixed(2),
      Number(r.balance).toFixed(2),
    ]),
  }), [filtered]);

  const toolbar = (
    <select
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      style={{
        padding: "8px 12px", borderRadius: 7,
        border: "1.5px solid rgba(180,140,80,.3)",
        background: "#fff", fontSize: 12, color: "#3a2a1a",
      }}>
      <option value="all">All statuses</option>
      <option value="pending">Pending</option>
      <option value="confirmed">Confirmed</option>
      <option value="checked_in">Checked in</option>
      <option value="checked_out">Checked out</option>
      <option value="cancelled">Cancelled</option>
      <option value="no_show">No show</option>
    </select>
  );

  return (
    <ReportShell
      title="Reservations Report"
      subtitle="Every booking in the selected window"
      range={{ ...range, onChange: onRangeChange }}
      csv={csv}
      filename="reservations"
      toolbar={toolbar}
    >
      {loading ? <Loading/> : (
        <>
          <div className="kpi-row">
            <StatTile icon="📋" label="Records"    value={filtered.length} sub={`${reservations.length} total in range`} />
            <StatTile icon="💵" label="Booked Value" value={fmtMoney(stats.total)} accent="#c8a05a" />
            <StatTile icon="✅" label="Collected"   value={fmtMoney(stats.paid)}  accent="#4a7c59" />
            <StatTile icon="⚠️" label="Outstanding" value={fmtMoney(stats.balance)} accent="#a93226" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginTop: 16 }}>
            <Panel title="By Status">     <PieChart data={stats.byStatus}/></Panel>
            <Panel title="By Source">     <BarChart data={stats.bySource}/></Panel>
          </div>
          <div style={{ marginTop: 16 }}>
            <Panel title="By Billing Mode"> <BarChart data={stats.byMode}/></Panel>
          </div>

          <Panel title={`Reservations (${filtered.length})`} style={{ marginTop: 18 }}>
            <table className="tbl" style={{ width: "100%" }}>
              <thead>
                <tr>
                  <th>Code</th><th>Guest</th><th>Room</th>
                  <th>In</th><th>Out</th><th>Status</th><th>Mode</th>
                  <th style={{ textAlign: "right" }}>Total</th>
                  <th style={{ textAlign: "right" }}>Paid</th>
                  <th style={{ textAlign: "right" }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15 }}>
                      {r.code}{r.booking_group_code && <span style={{ fontSize: 10, color: "#8a6a30", marginLeft: 4 }}>🧩 {r.booking_group_code}</span>}
                    </td>
                    <td>{r.guest_name}</td>
                    <td>{r.room_number || "—"}</td>
                    <td>{fmtDate(r.check_in_at || r.check_in_date)}</td>
                    <td>{fmtDate(r.check_out_at || r.check_out_date)}</td>
                    <td><StatusBadge status={r.status}/></td>
                    <td style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".08em", color: "#6b5142" }}>
                      {r.billing_mode || "nightly"}
                    </td>
                    <td style={{ textAlign: "right" }}>{fmtMoney(r.total_amount)}</td>
                    <td style={{ textAlign: "right" }}>{fmtMoney(r.paid_amount)}</td>
                    <td style={{ textAlign: "right", color: Number(r.balance) > 0 ? "#a93226" : "#2d6a4f" }}>
                      {fmtMoney(r.balance)}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={10} style={{ padding: 24, textAlign: "center", color: "#8a7060", fontStyle: "italic" }}>
                    No reservations match these filters.
                  </td></tr>
                )}
              </tbody>
            </table>
          </Panel>
        </>
      )}
    </ReportShell>
  );
}

function aggregate(list, keyFn) {
  const m = new Map();
  for (const r of list) {
    const k = keyFn(r) || "—";
    m.set(k, (m.get(k) || 0) + 1);
  }
  return Array.from(m.entries()).map(([label, value]) => ({ label, value }));
}
